import * as API from '@api';
import {
  DashboardSearchParams,
  SendingRequestBundle,
  SendingRequestDto,
  SendingsDashboardType,
} from '@domain/types';
import { createSendingRequestCache } from '@lifen-sending/utils/sendingRequestCache';
import { legacyFeatureFlagsStore } from '@services/featureFlags/legacyFeatureFlags.store';
import { logger } from '@services/logger/logger.service';
import { updateEntitiesAction } from '@store/documents/documents.actions';
import { selectDashboardSearchParams } from '@store/documents/documents.selector';
import {
  RealtimeCommunicationRequestUpdated,
  realtimeCommunicationUpdated,
} from '@store/externalEvents/externalEvents.action';
import { QueryClient } from '@tanstack/react-query';
import {
  findDocumentInCache,
  invalidateDashboardCache,
  updateDocumentInDashboardCache,
  updateDocumentInDetailCache,
} from '@utils/caching.util';
import { selectPathName } from '@utils/document.util';
import { getDashboardTypeFromRoute, isDetailRoute } from '@utils/route.utils';
import { all, getContext, put, select, takeLatest } from 'redux-saga/effects';

import { DocumentDashboardType } from '../documents/documents.state';

function receiveSendingUpdated() {
  return function* saga({ payload }: ReturnType<RealtimeCommunicationRequestUpdated>) {
    const queryClient: QueryClient = yield getContext('queryClient');

    const documentId = payload.resource.id;

    if (!documentId) return;

    const pathname: string = yield select(selectPathName);
    const currentDashboardType = getDashboardTypeFromRoute(pathname);
    const flags = legacyFeatureFlagsStore.get();
    const sendingRequestCache = createSendingRequestCache(queryClient, {
      useV2SendingDashboards: Boolean(flags['cafe-doc-use-v2-sending-endpoint']),
    });

    if (!currentDashboardType) {
      return;
    }

    try {
      if (
        currentDashboardType !== SendingsDashboardType.Inbox &&
        currentDashboardType !== SendingsDashboardType.Sent
      ) {
        // Invalidate inbox and sent cache without immediate refetch
        invalidateDashboardCache({
          dashboardType: SendingsDashboardType.Sent,
          type: 'all',
          refetchType: 'none',
          queryClient,
        });
        invalidateDashboardCache({
          dashboardType: SendingsDashboardType.Inbox,
          type: 'all',
          refetchType: 'none',
          queryClient,
        });
        // Inbox has no v2 pendant — only Sent needs the echo, and remoteChange sweeps it (with
        // Draft, harmlessly) under the flag; a no-op otherwise.
        sendingRequestCache.remoteChange(documentId);
        return;
      }

      const searchParams: DashboardSearchParams = yield select((state) =>
        selectDashboardSearchParams(state, currentDashboardType),
      );

      const sendingDtoFromCache = findDocumentInCache<DocumentDashboardType>({
        dashboardType: currentDashboardType,
        search: { searchParams, featureFlags: flags },
        documentId,
        queryClient,
      }) as SendingRequestDto | undefined;

      // findDocumentInCache only looks up the (legacy) dashboard list cache, which is not
      // populated when the Sent dashboard runs on the V2 query. Also check whether the
      // document's own detail page is currently open, so its cache still gets refreshed.
      const isCurrentDetailPage = isDetailRoute(pathname) && !!pathname.match(documentId);

      // Sending was found in cache, or its detail page is currently open
      if (sendingDtoFromCache || isCurrentDetailPage) {
        const bundle: SendingRequestBundle = yield API.fetchSendingRequest(
          documentId,
          currentDashboardType,
          true,
        );

        if (sendingDtoFromCache) {
          yield updateDocumentInDashboardCache({
            updatedDocument: bundle.document,
            updatedDocumentEntities: bundle.entities,
            dashboardType: currentDashboardType,
            queryClient,
          });
        }
        yield updateDocumentInDetailCache({ bundle, queryClient });
        yield put(updateEntitiesAction(bundle.entities));
      } else {
        // Sending was not found, invalidate all cache from other dashboard without immediate refetch
        invalidateDashboardCache({
          dashboardType:
            currentDashboardType === SendingsDashboardType.Inbox
              ? SendingsDashboardType.Sent
              : SendingsDashboardType.Inbox,
          type: 'all',
          refetchType: 'none',
          queryClient,
        });
        // Invalidate active cache for current dashboard with background fetching
        invalidateDashboardCache({
          dashboardType: currentDashboardType,
          type: 'all',
          refetchType: 'active',
          queryClient,
        });
        sendingRequestCache.remoteChange(documentId);
      }
    } catch (err) {
      logger.error('LOGIC', '[Sending Realtime] Failed to refresh sending', err);
    }
  };
}

export function* sendingRealTimeSagas() {
  yield all([takeLatest(realtimeCommunicationUpdated.type, receiveSendingUpdated())]);
}
